import { KeyboardEventHandler, useCallback, useEffect, useRef, useState } from "react";
import { AnserJsonEntry } from "anser";
import debounce from "lodash-es/debounce";
import { useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

import { useDebounce } from "hooks/useDebounce";

import ThemedLogsContent from "./Content";
import ThemedLogsSearchBar from "./SearchBar";
import ThemedLogsVirtualRow from "./VirtualRow";
import { transformAnsiToLines, findSearchOccurrences } from "./utils";

type ThemedLogsAnsiContentProps = {
  decoded: AnserJsonEntry[];
  searchMode?: boolean;
  setSearchMode?: (value: boolean) => void;
  fullScreen: boolean;
};

const ThemedLogsAnsiContentWithVirtualization = ({
  decoded,
  searchMode,
  setSearchMode,
  fullScreen,
}: ThemedLogsAnsiContentProps) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [searchPhrase, setSearchPhrase] = useState("");
  const debouncedSearchQuery = useDebounce(searchPhrase);
  const [selectedOccurrenceIndex, setSelectedOccurrenceIndex] = useState<number | undefined>(
    undefined
  );

  const transformedCode = useMemo(() => transformAnsiToLines(decoded), [decoded]);

  const searchOccurrences = useMemo(
    () => findSearchOccurrences(transformedCode, debouncedSearchQuery),
    [transformedCode, debouncedSearchQuery]
  );

  const currentOccurrenceIndex =
    selectedOccurrenceIndex !== undefined && selectedOccurrenceIndex < searchOccurrences.length
      ? selectedOccurrenceIndex
      : undefined;

  const count = transformedCode.length;
  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 45,
    paddingEnd: 16,
    paddingStart: 16,
    // Keeps rows mounted well beyond the viewport so a native text selection survives
    // scrolling during a drag instead of collapsing when its anchor row unmounts (CU-869d745bn).
    overscan: 50,
  });

  const items = virtualizer.getVirtualItems();

  useEffect(() => {
    if (!autoScroll) return;

    const frame = requestAnimationFrame(() => {
      virtualizer.scrollToOffset(virtualizer.getTotalSize());
    });

    return () => cancelAnimationFrame(frame);
  }, [count, autoScroll, virtualizer]);

  const handleContainerScroll = debounce(() => {
    if (!parentRef.current) return;

    const { scrollHeight, scrollTop, clientHeight } = parentRef.current;
    // Threshold accounts for subpixel rounding in virtualized row measurements
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 2;

    setAutoScroll(isAtBottom);
  }, 100);

  const handleSearchPhraseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchPhrase(e.target.value);
  };

  const handlePreviousPress = useCallback(() => {
    if (searchOccurrences.length === 0) return;

    const previousOccurrenceIndex =
      currentOccurrenceIndex !== undefined && currentOccurrenceIndex > 0
        ? currentOccurrenceIndex - 1
        : searchOccurrences.length - 1;

    setSelectedOccurrenceIndex(previousOccurrenceIndex);
    virtualizer.scrollToIndex(searchOccurrences[previousOccurrenceIndex].lineIndex, {
      align: "center",
    });
  }, [currentOccurrenceIndex, searchOccurrences, virtualizer]);

  const handleNextPress = useCallback(() => {
    if (searchOccurrences.length === 0) return;

    const nextOccurrenceIndex =
      currentOccurrenceIndex !== undefined && currentOccurrenceIndex < searchOccurrences.length - 1
        ? currentOccurrenceIndex + 1
        : 0;

    setSelectedOccurrenceIndex(nextOccurrenceIndex);
    virtualizer.scrollToIndex(searchOccurrences[nextOccurrenceIndex].lineIndex, {
      align: "center",
    });
  }, [currentOccurrenceIndex, searchOccurrences, virtualizer]);

  useEffect(() => {
    const [firstOccurrence] = searchOccurrences;

    setSelectedOccurrenceIndex(firstOccurrence ? 0 : undefined);

    if (firstOccurrence) {
      virtualizer.scrollToIndex(firstOccurrence.lineIndex, { align: "center" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  const handleInputKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement>>(
    (e) => {
      if (searchPhrase.length > 0 && e.key === "Enter") {
        e.preventDefault();
        if (e.shiftKey) {
          handlePreviousPress();
        } else {
          handleNextPress();
        }
      }
    },
    [handleNextPress, handlePreviousPress, searchPhrase]
  );

  const handleSearchBarClose = () => {
    setSearchMode?.(false);
    setSearchPhrase("");
    setSelectedOccurrenceIndex(undefined);
  };

  const getContainerHeight = () => {
    if (fullScreen) {
      return "calc(100vh - 125px)";
    }
    if (count * 15 >= 300) {
      return 300;
    }

    if (count === 1) {
      return 47; // 15px for the line + 16px padding + 16px for the search bar
    }

    return count * 15 + 16; // 15px per line + 16px padding
  };

  const containerHeight = getContainerHeight();

  return (
    <div
      ref={parentRef}
      onScroll={handleContainerScroll}
      style={{
        height: containerHeight,
        width: "100%",
        overflowY: "auto",
        contain: "strict",
      }}
    >
      {searchMode && (
        <ThemedLogsSearchBar
          searchPhrase={searchPhrase}
          searchOccurrences={searchOccurrences}
          currentOccurrenceIndex={currentOccurrenceIndex}
          onSearchPhraseChange={handleSearchPhraseChange}
          onPreviousPress={handlePreviousPress}
          onNextPress={handleNextPress}
          onClose={handleSearchBarClose}
          onKeyDown={handleInputKeyDown}
        />
      )}
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: "100%",
          position: "relative",
        }}
      >
        <ThemedLogsContent
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            margin: "0",
            padding: "0 16px",
            transform: `translateY(${items[0]?.start ?? 0}px)`,
          }}
        >
          {items.map((virtualRow) => (
            <ThemedLogsVirtualRow
              key={virtualRow.key}
              virtualRow={virtualRow}
              lineEntries={transformedCode[virtualRow.index]}
              searchText={debouncedSearchQuery}
              currentOccurrenceIndex={currentOccurrenceIndex}
              searchOccurrences={searchOccurrences}
              measureElement={virtualizer.measureElement}
            />
          ))}
        </ThemedLogsContent>
      </div>
    </div>
  );
};

export default ThemedLogsAnsiContentWithVirtualization;
